import { useCallback, useMemo, useState } from "react";

import {
    LendVaultSortType,
    StakeStatus,
    useLendVaults,
    UserVaultPositionExpanded,
    FetchLendVaultsInfoParams,
    StakeSortType,
    UserLendingVaultFilter
} from "@bridgesplit/abf-react";
import { useLocalStorage } from "@bridgesplit/react";
import { All, combineCollections, removeDuplicates, SortDirection, SortOrder } from "@bridgesplit/utils";
import { PaginationController } from "@bridgesplit/ui";

import { getPrincipalMints, useChunkedPaginationController } from "./shared";

const DEFAULT_SORT_SIDE = SortDirection.Desc;
const DEFAULT_SORT_TYPE = LendVaultSortType.TotalDeposits;
const DEFAULT_STAKE_SORT_TYPE = StakeSortType.WAvgApy;

export type LendingVaultPagination = ReturnType<typeof useLendingVaultPagination>;

export function useLendingVaultPagination() {
    const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>("LendVaultsOrder", DEFAULT_SORT_SIDE);
    const [sortType, setSortType] = useLocalStorage<LendVaultSortType>("LendVaultsSort", DEFAULT_SORT_TYPE);
    const [page, setPage] = useState<number>(0);
    const [principalMint, setPrincipalMint] = useState<string | undefined>(undefined);

    const tableController: PaginationController = {
        page,
        setPage,
        order: sortDirection === SortDirection.Asc ? SortOrder.Asc : SortOrder.Desc,
        setOrder: (order) => setSortDirection(order === SortOrder.Asc ? SortDirection.Asc : SortDirection.Desc),
        orderBy: sortType.toString(),
        setOrderBy: (orderBy) => {
            const order = parseInt(orderBy);
            if (!Object.values(LendVaultSortType).includes(order)) return;

            setSortType(order as LendVaultSortType);
        },
        resetPage: () => setPage(0)
    };

    const params: FetchLendVaultsInfoParams = {
        principalMints: principalMint ? [principalMint] : [],
        sortType,
        sortDirection,
        page,
        pageSize: 20
    };

    const reset = useCallback(() => {
        setSortDirection(DEFAULT_SORT_SIDE);
        setSortType(DEFAULT_SORT_TYPE);
        setPage(0);
    }, [setSortDirection, setSortType, setPage]);

    return {
        page,
        params,
        tableController,
        reset,
        principalMint,
        setPrincipalMint,
        setSortDirection,
        setSortType
    };
}

export function useFilteredLendVaults() {
    const pagination = useLendingVaultPagination();
    const {
        data: vaults,
        totalDataCount,
        isFetching
    } = useLendVaults({
        filter: { depositsEnabled: true, ...pagination.params }
    });

    const filteredVaults = useMemo(() => {
        if (!vaults) return { genesis: undefined, lendVaults: undefined };

        const publicVaults = vaults.filter((v) => !v?.vaultMetadata?.tags?.includes("urlAccessOnly"));
        const genesisVaults = publicVaults.filter((v) => v?.vaultMetadata?.tags?.includes("featured"));
        const nonGenesisVaults = publicVaults.filter((v) => !v?.vaultMetadata?.isGenesisVault);
        const lendVaults = removeDuplicates(combineCollections([genesisVaults, nonGenesisVaults]));

        return {
            genesis: genesisVaults,
            lendVaults
        };
    }, [vaults]);

    return { filteredVaults, totalDataCount, isFetching, pagination };
}

const DEFAULT_PAGE_SIZE = 5;

export function useLendingPositionPagination() {
    const [status, setStatus] = useState<StakeStatus>(StakeStatus.Active);
    const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>("LendVaultsStakeOrder", DEFAULT_SORT_SIDE);
    const [sortType, setSortType] = useLocalStorage<StakeSortType>("LendVaultsStakeSort", DEFAULT_STAKE_SORT_TYPE);
    const [principalMint, setPrincipalMint] = useState<string | undefined | All>(undefined);
    const [vaultAddresses, setVaultAddresses] = useState<string[]>([]);
    const [lpMints, setLpMints] = useState<string[]>([]);
    const [depositsEnabled, setDepositsEnabled] = useState<boolean | undefined>(undefined);

    const chunkedPagination = useChunkedPaginationController<UserVaultPositionExpanded, UserLendingVaultFilter>(
        {
            vaultAddresses,
            principalMints: getPrincipalMints(principalMint),
            lpMints,
            depositsEnabled,
            sort: sortType,
            sortDirection,
            status
        },
        DEFAULT_PAGE_SIZE
    );

    const reset = useCallback(() => {
        setStatus(StakeStatus.Active);
        setSortDirection(DEFAULT_SORT_SIDE);
        setSortType(DEFAULT_STAKE_SORT_TYPE);
        setVaultAddresses([]);
        setLpMints([]);
        setDepositsEnabled(undefined);
        chunkedPagination.resetPagination();
    }, [
        setSortDirection,
        setSortType,
        setStatus,
        chunkedPagination,
        setVaultAddresses,
        setLpMints,
        setDepositsEnabled
    ]);

    return {
        ...chunkedPagination,
        params: chunkedPagination.clientPagination,
        reset,
        principalMint,
        setStatus,
        setPrincipalMint,
        setSortDirection,
        setSortType,
        setVaultAddresses,
        setLpMints,
        setDepositsEnabled
    };
}
